import * as preact from "preact";

import * as chromeExtensionInstaller from "chromeExtensionInstaller";
import * as deviceDetect from "deviceDetect";
import * as iOSBridge from "iOSBridge";
import * as modalController from "modalController";
import * as languageMap from "shared/languageMap";
import stores from "stores/stores";
import SkeletonView from "views/SkeletonView";
import * as webReaderInstaller from "webReaderInstaller";
import * as webSearch from "webSearch";

class WebReaderInstallerDialog extends preact.Component {
  private installerRef = preact.createRef<HTMLDivElement>();

  componentDidMount() {
    if (this.installerRef.current) {
      webReaderInstaller.render(this.installerRef.current);
    }
  }

  render() {
    return (
      <>
        <h2>Install Web Reader</h2>
        <br />
        <div class="webReaderInstaller" ref={this.installerRef}></div>
      </>
    );
  }
}

interface State {
  popularDomains: webSearch.PopularDomain[] | null;
}

/** The websites popular with the language's learners: the Websites page */
class PopularDomainsView extends preact.Component<{}, State> {
  state: State = { popularDomains: null };

  async componentDidMount() {
    this.setState({
      popularDomains: await webSearch.fetchPopularDomains(
        stores.user.currentLearningLanguage.value
      ),
    });
  }

  installChromeExtension = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();
    chromeExtensionInstaller.install();
  };

  installWebReaderBookmarklet = (event: Event) => {
    event.stopPropagation();
    event.preventDefault();

    modalController.show(<WebReaderInstallerDialog />);
  };

  searchWeb = (event: Event) => {
    event.preventDefault();
    const query = new FormData(event.currentTarget as HTMLFormElement).get(
      "query"
    ) as string;
    if (query.trim()) {
      iOSBridge
        .get()
        ?.openBrowser(
          webSearch.searchURL(stores.user.currentLearningLanguage.value, query)
        );
    }
  };

  openPopularDomainLink = (event: MouseEvent) => {
    if (webSearch.openInAppBrowser(event)) {
      return;
    }

    // XXX Outdated? Remove??
    top.postMessage(
      JSON.stringify({
        message: "backgroundPageMessage",
        data: {
          message: "webReaderLoaded",
        },
      }),
      "*"
    );
  };

  render() {
    const popularDomains = this.state.popularDomains;
    const user = stores.user;
    const currentLearningLanguage = user.currentLearningLanguage.value;
    const currentLearningLanguageEnglishString =
      languageMap.map[currentLearningLanguage].english;

    // The iOS app's browser can open any site, so start with a web search
    const searchForm = deviceDetect.isIOSApp() ? (
      <form class="webSearch" onSubmit={this.searchWeb}>
        <input
          type="search"
          name="query"
          enterKeyHint="search"
          placeholder={`Search the web in ${currentLearningLanguageEnglishString}`}
        />
      </form>
    ) : null;

    if (popularDomains === null) {
      return (
        <div>
          {searchForm}
          <SkeletonView kind="blocks" count={4} />
        </div>
      );
    }

    // cspell: disable-next-line
    const chromeAvailable = !!(
      window.chrome && (window.chrome as any).webstore
    );
    const prominentWebReaderCallout = webReaderInstaller.showInstaller();
    const spanish = currentLearningLanguage === "es";
    const popularDomainsExist = popularDomains.length > 0;

    const spanishCallout = spanish ? (
      <p style={{ textAlign: "center", fontStyle: "italic" }}>
        Want more?{" "}
        <a href="http://blog.readlang.com/ultimate-guide-to-spanish-websites-for-beginners-to-practice-reading/">
          Check out this guide to popular Spanish websites for learners
        </a>
      </p>
    ) : null;

    if (!popularDomainsExist) {
      return (
        <div>
          {searchForm}
          <p style={{ margin: "40px auto" }}>
            No {currentLearningLanguageEnglishString} web sites have been added
            yet, but don't let that stop you,{" "}
            {deviceDetect.isIOSApp() ? (
              <>search the web above and tap any word to translate it.</>
            ) : (
              <>
                read any article you find on the web with the{" "}
                <a href="/webReader">Readlang Web Reader</a>.
              </>
            )}
          </p>
          {spanishCallout}
        </div>
      );
    }

    return (
      <div>
        {searchForm}
        <p>
          These are the most popular websites with{" "}
          {currentLearningLanguageEnglishString} learners on Readlang, it could
          be a good place to find new content to practice reading.
        </p>

        {deviceDetect.isIOSApp() ? (
          <p>Open a site, then tap any word to translate it.</p>
        ) : prominentWebReaderCallout ? (
          <div class="callout">
            Install the{" "}
            <a
              href="#"
              id="installWebReaderBookmarklet"
              onClick={this.installWebReaderBookmarklet}
            >
              Web Reader
            </a>{" "}
            first to benefit from Readlang's help when reading articles from
            these websites.
          </div>
        ) : (
          <small>
            <p>
              <i>
                Use the{" "}
                {chromeAvailable ? (
                  <a
                    href="#"
                    id="installChromeExtension"
                    onClick={this.installChromeExtension}
                  >
                    Chrome Extension
                  </a>
                ) : (
                  <a
                    href="#"
                    id="installWebReaderBookmarklet"
                    onClick={this.installWebReaderBookmarklet}
                  >
                    Web Reader
                  </a>
                )}{" "}
                to read articles from these (or any) websites.
              </i>
            </p>
          </small>
        )}

        <div class="sharedLinks"></div>
        <div>
          {popularDomains.map((domain) => (
            <div key={domain._id} class="popularDomain">
              <a
                class="popularDomainLink"
                href={webSearch.popularDomainURL(domain)}
                onClick={this.openPopularDomainLink}
              >
                {domain.title || domain.domainName}
              </a>
              <br />
              <small>{domain.domainName}</small>
              <br />
              <small class="description">{domain.description}</small>
            </div>
          ))}
        </div>

        {spanishCallout}
      </div>
    );
  }
}

export default PopularDomainsView;
